import React, { useCallback, useState } from 'react'

import moment from 'moment'

import { Flex, Paper } from '@driftt/dds'
import {
  Gantt,
  GanttViewTypeEnum,
  IFlagIndicator,
  IGanttOptions,
  ShapeEnum,
} from '@driftt/dds.reports'

import config from 'config'

import SideBar from './SideBar'
import useTeammateAvailabilityBuckets from './useTeammateAvailabilityBuckets.hook'

const WeeklyAvailabilityGanttLegends = [
  { name: 'Online', color: '$green2', border: '1px solid $green3' },
]

const DailyAvailabilityGanttLegends = [
  { name: 'Online', color: '$green2', border: '1px solid $green3' },
  { name: 'Missed chat', color: '$red6', shape: ShapeEnum.LINE },
  { name: 'Responded chat', color: '$green8', shape: ShapeEnum.LINE },
]

enum GanttViewTypeViewDaysEnum {
  'week' = 'isoWeek',
  'day' = 'day',
}

const TeammateAvailabilityTabContent = ({ teamFilters }) => {
  /** NOTE: today should be today since we have no data for the future. */
  const today = moment().startOf('day')

  const [viewFormat, setViewFormat] = useState<GanttViewTypeEnum>(
    GanttViewTypeEnum.DAY
  )

  const [viewStartsWithDate, setViewStartsWithDate] = useState<Date>(
    today.toDate()
  )

  const endDate = moment(viewStartsWithDate).endOf(
    GanttViewTypeViewDaysEnum[viewFormat]
  )

  const { isLoadingData, tableData } = useTeammateAvailabilityBuckets(
    viewStartsWithDate,
    viewFormat,
    teamFilters,
    viewStartsWithDate,
    endDate.toDate()
  )

  const onChangeOfView = useCallback(
    (date: Date, viewFormat: GanttViewTypeEnum) => {
      setViewStartsWithDate(date)
      setViewFormat(viewFormat)
    },
    []
  )

  const ganttOptions: IGanttOptions = {
    chart: {
      showBodyCellDashedDividers: true,
      isMiddleTimeLabelEnabled: !isLoadingData,
      weekIsMondayToSunday: true,
      showLastDayOfWeekWhenSwitchingToDayView: true,
      defaultViewType: 'day',
      loadingState: {
        dateNavigationHeader: {
          renderSkeleton: true,
        },
      },
      emptyState: {
        minHeight: 300,
        message:
          'Select a different date range or modify the filters to see this chart',
        title: 'No data for this timeframe',
      },
      availableDateRange: {
        end: today.toDate(), // NOTE: this should prevent users from navigating to future dates
      },
      scrollToColumnIndexOnLoad: 8,
    },
    legends: {
      successLabel: 'responded chat at ',
      errorLabel: 'missed chat at ',
    },
  }

  const redirectToChatPage = (chat: IFlagIndicator) => {
    if (chat?.id) {
      const chatURL = `${config.APP_HOMEPAGE}/conversations/${chat?.id}`
      window.open(chatURL, '_blank')
    }
  }

  const handleOnNextButtonClick = (date: Date) => {
    setViewStartsWithDate(date)
  }

  const handleOnPreviousButtonClick = (date: Date) => {
    setViewStartsWithDate(date)
  }

  return (
    <Paper
      fullWidth
      elevation={2}
      as={Flex}
      direction="column"
      alignItems={'stretch'}
      css={{ paddingTop: '$4' }}
    >
      <Gantt
        rows={tableData}
        initialDate={viewStartsWithDate}
        isLoading={isLoadingData}
        onGanttViewChangeCallback={onChangeOfView}
        legends={
          viewFormat === GanttViewTypeEnum.WEEK
            ? WeeklyAvailabilityGanttLegends
            : DailyAvailabilityGanttLegends
        }
        options={ganttOptions}
        onFlagIndicatorClickCallback={redirectToChatPage}
        onNextButtonClickCallback={handleOnNextButtonClick}
        onPreviousButtonClickCallback={handleOnPreviousButtonClick}
      >
        <SideBar />
      </Gantt>
    </Paper>
  )
}

export default TeammateAvailabilityTabContent
